import React, { useState, useEffect } from 'react';
import apiFetch from '../../../../utils/api';
import Preloader from '../../../../components/Preloader/Preloader';
import Pagination from '../../../../components/Pagination/Pagination';
import './AdminStyles.css';

const AdminAvailablePins = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, available: 0, used: 0, unassigned: 0 });
  const [acting, setActing] = useState(null);
  const [showGen, setShowGen] = useState(false);
  const [pendingPinType, setPendingPinType] = useState(null); // 'real' | 'system' — set when opening modal
  const [genCount, setGenCount] = useState(5);
  const [assignTo, setAssignTo] = useState('');
  const [assignToFocused, setAssignToFocused] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState('');
  const [activeTab, setActiveTab] = useState(''); // '' = All, 'Available', 'Used'
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState('');

  const fetchData = () => {
    setLoading(true);
    let url = `/api/admin/pins/available?page=${page}`;
    if (activeTab) url += `&status=${activeTab}`;

    apiFetch(url)
      .then(res => res.json())
      .then(res => {
        setData(res.pins || []);
        setTotalPages(res.totalPages || 1);
        setStats(res.stats || { total: 0, available: 0, used: 0, unassigned: 0 });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(fetchData, [page, activeTab]);

  useEffect(() => {
    apiFetch('/api/admin/users?limit=1000')
      .then(res => res.json())
      .then(res => setUsers(res.users || []))
      .catch(() => {});
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const openGenModal = (pinType) => {
    setPendingPinType(pinType);
    setGenCount(5);
    setAssignTo('');
    setGenMsg('');
    setShowGen(true);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('');
    try {
      const res = await apiFetch('/api/admin/pins/create', {
        method: 'POST',
        body: JSON.stringify({ count: genCount, assignTo, pin_type: pendingPinType }),
      });
      const d = await res.json();
      setGenMsg(d.message);
      if (d.success) { fetchData(); }
    } catch {
      setGenMsg('Server error.');
    }
    setGenerating(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pin?')) return;
    setActing(id);
    const res = await apiFetch(`/api/admin/pins/${id}/delete`, { method: 'DELETE' });
    const d = await res.json();
    if (d.success) fetchData();
    setActing(null);
  };

  const handleClearDeposits = async () => {
    setClearing(true);
    setClearMsg('');
    try {
      const res = await apiFetch('/api/admin/pins/clear-deposits', { method: 'POST' });
      const d = await res.json();
      setClearMsg(d.message);
      setShowClearConfirm(false);
    } catch {
      setClearMsg('Server error while clearing data.');
    }
    setClearing(false);
  };

  const isRealModal = pendingPinType === 'real';

  return (
    <div className="admin-page">
      <div className="admin-breadcrumb">🏠 / Admin / E-Pins Management</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 className="admin-title" style={{ marginBottom: 0 }}>E-Pins Management</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="admin-btn admin-btn--approve"
            style={{ padding: '8px 16px', fontSize: 13 }}
            onClick={() => openGenModal('real')}
          >
            💳 Real Pin
          </button>
          <button
            className="admin-btn admin-btn--edit"
            style={{ padding: '8px 16px', fontSize: 13 }}
            onClick={() => openGenModal('system')}
          >
            🔧 System Allocated Pin
          </button>
          <button
            className="admin-btn admin-btn--reject"
            style={{ padding: '8px 16px', fontSize: 13 }}
            onClick={() => { setShowClearConfirm(true); setClearMsg(''); }}
          >
            🗑 Clear Balance Data
          </button>
        </div>
      </div>

      {clearMsg && (
        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 6, fontSize: 13,
          background: clearMsg.includes('cleared') ? 'var(--awp-success-soft)' : 'var(--awp-danger-soft)',
          color: clearMsg.includes('cleared') ? 'var(--awp-success)' : 'var(--awp-danger)',
          border: `1px solid ${clearMsg.includes('cleared') ? 'var(--awp-success)' : 'var(--awp-danger)'}`,
        }}>
          {clearMsg}
        </div>
      )}

      <div className="admin-stats-bar" style={{ marginTop: 16 }}>
        <div className="admin-stat-chip blue">{stats.total} Total</div>
        <div className="admin-stat-chip green">{stats.available} Available</div>
        <div className="admin-stat-chip gray">{stats.used} Used</div>
        {stats.unassigned > 0 && (
          <div className="admin-stat-chip red">{stats.unassigned} Unassigned</div>
        )}
      </div>

      {/* Tab Buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['', '📋 All Pins'], ['Available', '✅ Available Pins'], ['Used', '🔒 Used Pins']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => handleTabChange(val)}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
              background: activeTab === val
                ? val === '' ? 'linear-gradient(135deg, var(--awp-accent), var(--awp-accent-hover))'
                  : val === 'Available' ? 'linear-gradient(135deg, var(--awp-success), var(--awp-success-border))'
                  : 'linear-gradient(135deg, var(--awp-text-subtle), var(--awp-text-muted))'
                : 'var(--awp-surface-2)',
              color: activeTab === val ? 'var(--awp-accent-contrast)' : 'var(--awp-text-muted)',
              boxShadow: activeTab === val ? 'var(--awp-shadow-sm)' : 'none',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          {activeTab === 'Available' ? 'Available E-Pins' : activeTab === 'Used' ? 'Used E-Pins' : 'All E-Pins'}
        </div>
        <div className="admin-card-body">
          {loading ? (
            <Preloader message="Fetching E-Pins..." fullScreen={false} />
          ) : data.length === 0 ? (
            <div className="admin-empty">
              {activeTab === 'Available' ? 'No available pins' : activeTab === 'Used' ? 'No used pins' : 'No pins found'}
            </div>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Pin Code</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Assigned To</th>
                      <th>Used By</th>
                      <th>TRX ID</th>
                      <th>Created</th>
                      <th>Expires At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(row => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td><code style={{ background: 'var(--awp-surface-2)', padding: '2px 8px', borderRadius: 4, color: 'var(--awp-text-strong)' }}>{row.code}</code></td>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                            background: row.pinType === 'system' ? 'var(--awp-warning-soft)' : 'var(--awp-success-soft)',
                            color: row.pinType === 'system' ? 'var(--awp-warning)' : 'var(--awp-success)',
                          }}>
                            {row.pinType === 'system' ? '🔧 System' : '💳 Real'}
                          </span>
                        </td>
                        <td><span className={`status-badge ${row.status === 'Available' ? 'available' : 'used'}`}>{row.status}</span></td>
                        <td>{row.assignedTo || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td>{row.usedBy || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td>{row.trxId || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td>{row.createdAt}</td>
                        <td>{row.expiresAt || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td><button className="admin-btn admin-btn--reject" onClick={() => handleDelete(row.id)} disabled={acting === row.id}>🗑</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGen && (
        <div className="admin-modal-overlay" onClick={() => setShowGen(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>
                {isRealModal ? '💳 Generate Real Pins' : '🔧 Generate System Allocated Pins'}
              </h3>
              <button className="admin-modal-close" onClick={() => setShowGen(false)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                background: isRealModal ? 'var(--awp-success-soft)' : 'var(--awp-warning-soft)',
                color: isRealModal ? 'var(--awp-success)' : 'var(--awp-warning)',
                border: `1px solid ${isRealModal ? 'var(--awp-success)' : 'var(--awp-warning)'}`,
              }}>
                {isRealModal
                  ? '💳 Real pins — PKR 750 each counted in platform available balance.'
                  : '🔧 System pins — value = PKR 0. Not counted in balance. For testing or manual allocation only.'}
              </div>

              {genMsg && (
                <div style={{ padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13,
                  background: genMsg.includes('created') ? 'var(--awp-success-soft)' : 'var(--awp-danger-soft)',
                  color: genMsg.includes('created') ? 'var(--awp-success)' : 'var(--awp-danger)' }}>
                  {genMsg}
                </div>
              )}
              <label>Number of Pins (1-100)</label>
              <input type="number" min={1} max={100} value={genCount} onChange={e => setGenCount(e.target.value)} />

              <label style={{ marginTop: 12, display: 'block' }}>Assign to User (optional)</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search user by email or username..."
                  value={assignTo}
                  onChange={e => setAssignTo(e.target.value)}
                  onFocus={() => setAssignToFocused(true)}
                  onBlur={() => setTimeout(() => setAssignToFocused(false), 200)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--awp-border)', fontSize: 13, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
                  autoComplete="off"
                />
                {assignToFocused && !users.find(u => u.username === assignTo) && (
                  <ul style={{
                    position: 'absolute', top: '100%', left: 0, width: '100%',
                    background: 'var(--awp-surface)', border: '1px solid var(--awp-border)', borderRadius: '4px',
                    maxHeight: '150px', overflowY: 'auto', zIndex: 10, listStyle: 'none', margin: 0, padding: 0,
                    boxShadow: 'var(--awp-shadow-md)'
                  }}>
                    {users.filter(u => u.username.toLowerCase().includes(assignTo.toLowerCase()) || u.email.toLowerCase().includes(assignTo.toLowerCase())).map(u => (
                      <li key={u.id} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--awp-divider)', fontSize: 13 }}
                        onMouseDown={() => setAssignTo(u.username)}>
                        {u.username} <span style={{ color: 'var(--awp-text-muted)' }}>({u.email})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p style={{ fontSize: 11, color: 'var(--awp-text-subtle)', marginTop: 6 }}>
                If assigned, the user will see these pins in their "Available E-Pins" page.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button
                className={`admin-btn ${isRealModal ? 'admin-btn--approve' : 'admin-btn--edit'}`}
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? 'Generating...' : `Generate ${genCount} ${isRealModal ? 'Real' : 'System'} Pin(s)`}
              </button>
              <button className="admin-btn admin-btn--edit" onClick={() => setShowGen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Balance Confirmation Modal */}
      {showClearConfirm && (
        <div className="admin-modal-overlay" onClick={() => setShowClearConfirm(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="admin-modal-header">
              <h3>⚠️ Clear Balance Data</h3>
              <button className="admin-modal-close" onClick={() => setShowClearConfirm(false)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <p style={{ fontSize: 13, color: 'var(--awp-text)', marginTop: 0 }}>
                This will delete all <strong>E-Pin Mint deposit records</strong> that were auto-created when pins were generated. The platform available balance will be recalculated from scratch based on <strong>real pins only (PKR 750 each)</strong>.
              </p>
              <p style={{ fontSize: 13, color: 'var(--awp-danger)', fontWeight: 600 }}>
                This action cannot be undone. Use only to fix incorrect balance data.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn--reject" onClick={handleClearDeposits} disabled={clearing}>
                {clearing ? 'Clearing...' : 'Yes, Clear Data'}
              </button>
              <button className="admin-btn admin-btn--edit" onClick={() => setShowClearConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAvailablePins;
